import React from "react";
import { Input } from "best-common-react";
import clsx from "clsx";

function sanitizePersonNameLive(input: string): string {
  if (/^n\/a$/i.test(input)) {
    return "N/A";
  }

  return input
    .normalize("NFC")
    .replace(/[^\p{L}\p{M} .'\-/]/gu, "")
    .replace(/([.'\-\/ ]){2,}/g, "$1");
}

function sanitizePersonNameFinal(input: string): string {
  if (/^n\/a$/i.test(input)) {
    return "N/A";
  }

  return input
    .normalize("NFC")
    .replace(/[^\p{L}\p{M} .'\-/]/gu, "")
    .replace(/([.'\-\/ ]){2,}/g, "$1")
    .replace(/^[.'\-\/ ]+|[.'\-\/ ]+$/g, "");
}

const NAME_REGEX = /^[\p{L}\p{M}](?:[\p{L}\p{M} .'\-/]*[\p{L}\p{M}])?$/u;

export function isValidPersonName(s: string): boolean {
  const n = s.normalize("NFC").trim();

  if (/^n\/a$/i.test(n)) {
    return true;
  }

  if (n.length < 2 || n.length > 80) return false;
  if (/(?:[.'\-\/ ]){2,}/u.test(n)) return false; // still disallow double separators
  return NAME_REGEX.test(n);
}

type Props = {
  id: string;
  label: string;
  value?: string;
  onChange: (value?: string) => void;
  required?: boolean;
  className?: string;
  showNA?: boolean;
};

const NameInput: React.FC<Props> = ({ id, label, value, onChange, className, required = false, showNA = false }) => {
  const [touched, setTouched] = React.useState(false);

  const handleChange = (updated?: string) => {
    onChange(sanitizePersonNameLive(updated));
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text") ?? "";
    const cleaned = sanitizePersonNameLive(text);
    const el = e.currentTarget;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + cleaned + el.value.slice(end);
    onChange(sanitizePersonNameLive(next));
    // restore caret after React state flush
    requestAnimationFrame(() => {
      el.setSelectionRange(start + cleaned.length, start + cleaned.length);
    });
  };

  const onDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onChange(sanitizePersonNameFinal(e.currentTarget.value));
    setTouched(true);
  };

  const valid = isValidPersonName(value);

  return (
    <Input
      id={id}
      type="text"
      label={label}
      className={clsx(touched && !valid ? "is-invalid" : "", className || "")}
      value={value || ""}
      onChange={handleChange}
      // @ts-ignore
      onPaste={onPaste}
      onDrop={onDrop}
      onBlur={onBlur}
      required={required}
      showNA={showNA}
    />
  );
};

export default NameInput;
